import { BotConfigResolver } from '@justrelate/ai-assistant';
import { Chatbot } from '@justrelate/chatbot';
import { useState } from 'react';
import {
  ContentTag,
  CurrentPage,
  LinkTag,
  NotFoundErrorPage,
  canWrite,
  connect,
  ensureUserIsLoggedIn,
  isEditorLoggedIn,
  isInPlaceEditingActive,
  provideComponent,
  provideEditingConfig,
  provideLayoutComponent,
  provideObjClass,
  provideWidgetClass,
} from 'scrivito';

import { getInstanceIdFromPath } from './getInstanceIdFromPath';

provideObjClass('Download', {
  attributes: {
    blob: 'binary',
    tags: 'stringlist',
    title: 'string',
  },
});

const TextWidget = provideWidgetClass('TextWidget', {
  attributes: { text: 'html' },
  extractTextAttributes: ['text'],
});

provideComponent(TextWidget, ({ widget }) => (
  <ContentTag attribute="text" content={widget} tag="div" />
));

const BotConfig = provideObjClass('BotConfig', {
  attributes: {
    botName: 'string',
    prompt: ['widgetlist', { only: 'TextWidget' }],
    greeting: 'string',
    resources: 'referencelist',
    temperature: 'float',
  },
  extractTextAttributes: ['prompt'],
});

provideEditingConfig(BotConfig, {
  title: 'Bot Configuration',
  attributes: {
    botName: { title: 'Bot Name' },
    prompt: { title: 'Bot Prompt' },
    resources: { title: 'Document List' },
    temperature: {
      description:
        "Adjust the decimal value between 0 (conservative) and 1 (creative) to control the variability of the bot's responses. Default: 1.",
    },
  },
  properties: ['botName', 'temperature', 'resources'],
  initialContent: { temperature: 1 },
  validations: [
    [
      'temperature',
      (temperature) => {
        if (
          typeof temperature === 'number' &&
          (temperature < 0 || temperature > 1)
        ) {
          return 'Temperature must be between 0 and 1.';
        }
      },
    ],
  ],
});

provideLayoutComponent(BotConfig, () => {
  return (
    <>
      <a href={`/${getInstanceIdFromPath()}`}>Back to Bot Overview</a>
      <CurrentPage />
    </>
  );
});

provideComponent(
  BotConfig,
  ({ page: botConfig }) => {
    const [showChatbot, setShowChatbot] = useState(true);
    const resolvedConfig = new BotConfigResolver(botConfig);

    if (isInPlaceEditingActive()) {
      return (
        <>
          <h2> Bot Configuration </h2>
          <div style={{ backgroundColor: '#eee' }}>
            <em>
              Bot Name: <small>(edit here)</small>
            </em>
            <ContentTag tag="h4" attribute="botName" content={botConfig} />
          </div>
          <div style={{ backgroundColor: '#eee' }}>
            <em>
              System Prompt: <small>(edit here)</small>
            </em>
            <ContentTag attribute="prompt" content={botConfig} />
          </div>
          <br />
          <div style={{ backgroundColor: '#eee' }}>
            <em>
              Greeting Message: <small>(edit here)</small>
            </em>
            <ContentTag tag="div" attribute="greeting" content={botConfig} />
          </div>
          <br />
          <div style={{ backgroundColor: '#eee' }}>
            <em>
              Document List: <br />
              <small>(edit via sidebar properties)</small>
            </em>
            <ul>
              {resolvedConfig
                .resourcesMetadata()
                .map(({ title, context }, idx) => (
                  <li key={idx}>
                    <strong>{title}</strong>
                    <br />
                    {context}
                  </li>
                ))}
            </ul>
          </div>
        </>
      );
    }

    const name = botName(botConfig);
    const systemPrompt = resolvedConfig.systemPrompt();

    return (
      <>
        <h1>Preview of {name}</h1>
        <p>System Prompt:</p>
        <pre style={{ whiteSpace: 'pre-wrap' }}>{systemPrompt}</pre>
        <p>Greeting Message:</p>
        <ContentTag tag="div" attribute="greeting" content={botConfig} />

        <Chatbot
          config={{
            temperature: resolvedConfig.temperature(),
            systemPrompt,
            tools: [resolvedConfig.resourcesReadTool()],
            initialMessages: resolvedConfig.greeting()
              ? [{ role: 'assistant', content: resolvedConfig.greeting() }]
              : [],
          }}
          isVisible={showChatbot}
          onHide={() => setShowChatbot(false)}
          showHistory
        />

        {!showChatbot && (
          <div
            className="jr-ai-assist-floating-button"
            onClick={() => setShowChatbot(true)}
          >
            <span>Chatbot</span>
          </div>
        )}
      </>
    );
  },
  { loading: () => null },
);

export const BotAcademy = connect(() => {
  ensureUserIsLoggedIn();

  const botObjs = BotConfig.where('_parentPath', 'equals', '/').take();

  return (
    <>
      <NotFoundErrorPage>
        <h1>JustRelate Bot Academy</h1>
        <p>These are the bots that have been created:</p>
        {botObjs.length === 0 ? (
          <p>
            <em>No bots published yet.</em>
          </p>
        ) : (
          <ul>
            {botObjs.map((botObj) => (
              <li key={botObj.id()}>
                <LinkTag to={botObj}>{botName(botObj)}</LinkTag>
              </li>
            ))}
          </ul>
        )}
        {canWrite() && (
          <button
            onClick={() => BotConfig.create({ _path: `/${Math.random()}` })}
          >
            Create New Bot
          </button>
        )}
        {!isEditorLoggedIn() && (
          <button
            onClick={() =>
              (window.location.href = `https://edit.scrivito.com/${window.location}`)
            }
          >
            Manage Bots in Web Builder
          </button>
        )}
      </NotFoundErrorPage>
      <CurrentPage />
    </>
  );
});

function botName(obj: InstanceType<typeof BotConfig>) {
  return obj.get('botName') || `Untitled Bot ${obj.id()}`;
}
